import type { WidgetDefinition } from "@/types";

/**
 * Apply a saved key order to a list of widgets.
 *
 * The twin of `orderSections` in components/layout/nav-sections.ts, and it
 * tolerates the same drift: widgets present in `order` sort by their position in
 * it; a widget NOT in `order` (newly shipped, or newly permitted) keeps its
 * catalog position and falls to the end; stale keys that map to no widget are
 * ignored.
 *
 * Used ONLY by the customize sheet, to order its own draft list while the user
 * drags. The grid itself renders the `visible` order the backend already
 * resolved — that rule (which widgets show at all) lives server-side, in one
 * place, deliberately.
 */
export function orderWidgets(
  widgets: WidgetDefinition[],
  order: string[],
): WidgetDefinition[] {
  const rank = new Map(order.map((key, index) => [key, index]));

  return widgets
    .map((widget, index) => ({ widget, index }))
    .sort((a, b) => {
      const ra = rank.get(a.widget.key) ?? Infinity;
      const rb = rank.get(b.widget.key) ?? Infinity;
      // Ties (both unranked) keep the catalog order the backend sent.
      return ra - rb || a.index - b.index;
    })
    .map(({ widget }) => widget);
}
